import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {ConsoleConfig} from '../../../../webmate/shared/console/interfaces/console-config.interface';
import {catchError, ignoreElements, map, mergeMap, switchMap, takeUntil, tap} from 'rxjs/operators';
import {WebmateDevice} from './wm-types.interface';
import {
    ConsoleSessionId,
    DeviceId,
    ScreenshotId,
    VideoRecordingId
} from '@webmate-web-core/shared/types/ids';
import {ConsoleSession, consoleSessionFromBackendObject} from '@webmate-web-core/shared/types/console-session';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {EventsService, WebmateWebsocketEvent} from '@webmate-web-core/shared/services/events/events.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {DeviceSlotReservation} from '@webmate-web-core/shared/types/devices/device-slot-reservation';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';

@Injectable()
export class ConsoleService {

    constructor(private http: HttpClient, private notificationService: WmNotificationService,
                private eventService: EventsService, private userService: UserService,
                private deviceService: DeviceService) {
    }

    private handleEvents(config: ConsoleConfig, e: WebmateWebsocketEvent, terminationHandler?: () => void): Observable<any> {
        // Check if the event is related to the current device
        if (e.eventType === 'DeviceConsole.Created' && e.deviceId === config.deviceId) {
            if (e.userId !== config.userId) {
                return this.userService.getInformationAboutUser(e.userId).pipe(tap(u => this.notificationService.toastNotification(`User ${u.name} is now also using this console.`)))
            }
        } else if (e.eventType === 'DeviceConsole.Terminated' && e.session.deviceId === config.deviceId) {
            if (e.session.id !== config.sessionId) {
                // Other session on the same device was terminated, check if the user is the same
                // This happens e.g. if an older session timeouts
                if (e.session.userId !== config.userId) {
                    return this.userService.getInformationAboutUser(e.session.userId).pipe(tap(u => this.notificationService.toastNotification(`User ${u.name} has left this console.`)))
                }
            } else if (terminationHandler) {
                terminationHandler();
            } else {
                this.notificationService.toastNotification('Your session has been ended by the server');
            }
        } else if (e.eventType.startsWith('DeviceSlotReservation.') && e.slot.optDevice === config.deviceId) {
            const reservation = e['reservation'] as DeviceSlotReservation;
            if (e.eventType === 'DeviceSlotReservation.Warn' || e.eventType === 'DeviceSlotReservation.Info') {

                let begin = Date.parse(reservation.nextActiveAt!);
                let diffMins = Math.round((begin - Date.now()) / 60000); // minutes

                if (config.userId === reservation.userId) {
                    // our own reservation is about to start
                    this.notificationService.toastNotification('Your reservation of this slot will begin in ' + diffMins + ' minutes.');
                } else {
                    // a reservation by another user is about to start
                    return this.userService.getInformationAboutUser(reservation.userId).pipe(tap(u => this.notificationService.toastWarningNotification('The reservation of ' + u.name + ' for this slot will begin in ' + diffMins + ' minutes.\nPlease finish your work now.')));
                }

            } else if (e.eventType === 'DeviceSlotReservation.Begin') {
                if (config.userId === reservation.userId) {
                    // our own reservation has started
                    this.notificationService.toastNotification('Your reservation for this slot has now started.');
                } else {
                    // a reservation by another user will start soon
                    return this.userService.getInformationAboutUser(reservation.userId).pipe(tap(u => this.notificationService.toastWarningNotification('The reservation of ' + u.name + ' for this slot has started.\nYou will be logged out automatically.')));
                }
            }
        }
        // Fallback for sync handlers
        return of(undefined);
    }

    getClipboardContent(id: ConsoleSessionId): Observable<any> {
        return this.http.get('/console/' + id + '/clipboard', {responseType: 'text'}).pipe(map(x => JSON.parse(x)));
    }

    setClipboardContent(id: ConsoleSessionId, s: string): Observable<any> {
        return this.http.post('/console/' + id + '/clipboard', s);
    }

    startEventPolling(config: ConsoleConfig, terminationHandler?: () => void) {
        return this.eventService.getEventsForProject(config.projectId).pipe(mergeMap(e => this.handleEvents(config, e, terminationHandler)));
    }

    sendKeepAlive(sessionId: ConsoleSessionId) {
        return this.http.post(`/console/${sessionId}/log`, {}, {responseType: 'text'});
    }

    setScreenSecurity(deviceId: DeviceId, properties: any, enabled: boolean) {
        return this.imposeDevicePropertyRequirements(deviceId, properties, [
            {propertyName: DevicePropertyNames.ScreenSecurity, value: enabled}
        ]);
    }

    setKeychainBypass(deviceId: DeviceId, properties: any, enabled: boolean) {
        return this.imposeDevicePropertyRequirements(deviceId, properties, [
            {propertyName: DevicePropertyNames.KeychainBypass, value: enabled}
        ]);
    }

    setSensorsimulation(deviceId : DeviceId, properties: any, simulate: boolean, accept: boolean) {
        return this.imposeDevicePropertyRequirements(deviceId, properties, [
            {propertyName: DevicePropertyNames.SimulateBiometrics, value: simulate},
            {propertyName: DevicePropertyNames.BiometricAuthentication, value: accept}
        ]);
    }

    takeDeviceScreenshot(config: ConsoleConfig): Observable<ScreenshotId> {
        return this.http.post<ScreenshotId>('/device/devices/' + config.deviceId + '/screenshot', '');
    }

    setCameraSimulation(deviceId: DeviceId, properties: any, simulate: boolean, selectedImage: ScreenshotId, imagePool?: ScreenshotId[]) {
        const cameraSimulation = {
            enabled: simulate,
            // angular http ignores undefined values, so use null to explicitly unset the value
            selectedImage: selectedImage ?? null
        };
        const updates: Array<{propertyName: string, value: any}> = [{
            propertyName: DevicePropertyNames.SimulateCamera,
            value: cameraSimulation
        }];

        if (imagePool) {
            updates.push({
                propertyName: DevicePropertyNames.MediaSettings,
                value: {imagePool}
            });
        }

        return this.imposeDevicePropertyRequirements(deviceId, properties, updates);
    }

    setImagePool(deviceId: DeviceId, properties: any, imagePool: ScreenshotId[]) {
        return this.imposeDevicePropertyRequirements(deviceId, properties, [{
            propertyName: DevicePropertyNames.MediaSettings,
            value: {imagePool}
        }]);
    }

    setGPSCoordinates(deviceId : DeviceId, properties: any, state: boolean, latitude: number, longitude: number) {
        return this.imposeDevicePropertyRequirements(deviceId, properties, [{
            propertyName: DevicePropertyNames.SimulateGPS,
            value: {
                active: state,
                latitude: latitude,
                longitude: longitude
            }
        }]);
    }

    /**
     * Retrieves a list of all devices that are available for being used by mobile bridge
     */
    getAvailableMobileBridgeDevicesForDevice(deviceId: DeviceId): Observable<WebmateDevice[]> {
        return this.http.get<WebmateDevice[]>('/device/devices/' + deviceId + '/availableMobileBridgeDevices');
    }

    setDeviceRequirement(deviceId: DeviceId, requirementName: string, value: any): Observable<any> {
        return this.http.put(`/device/devices/${deviceId}/requirements/${requirementName}`, value);
    }

    startVideoRecording(deviceId: string): Observable<VideoRecordingId> {
        return this.http.post<VideoRecordingId>('/device/devices/' + deviceId + '/videorecording/start', {});
    }

    stopVideoRecording(videoRecordingId: VideoRecordingId): Observable<any> {
        return this.http.post('/videos/' + videoRecordingId + '/stop', {});
    }

    uploadImageToDevice(deviceId: DeviceId, screenshotId: ScreenshotId): Observable<any> {
        return this.http.post('/device/' + deviceId + '/image/' + screenshotId, {});
    }

    requestConsoleSession$(deviceId: DeviceId): Observable<ConsoleSession> {
        return this.http.post<any>(`/device/devices/${deviceId}/console`, {}).pipe(map(response => consoleSessionFromBackendObject(response)));
    }

    private imposeDevicePropertyRequirements(
        deviceId: DeviceId,
        properties: any,
        updates: Array<{propertyName: string, value: any}>
    ): Observable<any> {
        return this.deviceService.imposeDevicePropertyRequirements(deviceId, updates).pipe(
            map(device => ({
                ...properties,
                ...device.properties
            }))
        );
    }

    getCanvas(config: ConsoleConfig): HTMLCanvasElement {
        switch (config.type) {
            case 'VCloudTestCenter':
                return document.getElementById('mainCanvas') as HTMLCanvasElement;
            case 'OpenSTF':
                return document.getElementById('screen') as HTMLCanvasElement;
            case 'macvirt':
                return document.getElementById('mac-canvas') as HTMLCanvasElement;
        }
        throw new Error('Could not find canvas');
    }

}
